import { E } from '@angular/cdk/keycodes';
import { Injectable } from '@angular/core';
import {
    AnnualReportExcelRow,
    AnnualReportExcelSheet,
    AnnualReportExcelSheetTable,
    IBalanceSheetData,
    IFinancialTableData,
    IPdfTronData,
    ISectionData,
    ISignatureData,
    PDFSectionIds,
    RowTypes,
    SuperTableStructuredData,
} from '@modeso/aumico-lib-annualreport-fe';
import { EMPTY_ARRAY_LENGTH, NumberUtils } from '@modeso/aumico-lib-core-fe';
import { INotesSection } from '@modeso/types__aumico-lib-annualreport-be/dist/models/interfaces/INotesSection.interface';
import { ComponentType, SuperTableColumnType } from '@modeso/types__aumico-lib-notes-be';
import { INotesComponentsNumberedTitles } from '@modeso/aumico-lib-notes-fe';
import { SuperTableHelper } from '@modeso/aumico-lib-notes-fe/helpers/super-table.helper';
import { IFxPrintRow, IFxSuperTablePrintContext } from '@modeso/aumico-lib-notes-fe';
import { NotesDisplayMapper } from '@modeso/aumico-lib-notes-fe/mappers/notes-display.mapper';
import { SuperTableMapper } from '@modeso/aumico-lib-notes-fe/mappers/super-table.mapper';
import { IAnnualReport } from '@modeso/types__aumico-lib-annualreport-be';
const SECTIONS_tO_EXPORT_AS_EXCEL = [
    PDFSectionIds.BALANCE,
    PDFSectionIds.INCOME,
    PDFSectionIds.CASHFLOW,
    PDFSectionIds.AOP,
    PDFSectionIds.APBS,
    PDFSectionIds.APIS,
];

const SECTIONS_WITH_NO_REFERENCE = [PDFSectionIds.AOP];
@Injectable({ providedIn: 'root' })
export class ExcelMapper {
    constructor(
        private readonly notesDisplayMapper: NotesDisplayMapper,
        private readonly superTableMapper: SuperTableMapper,
    ) {}

    public mapPdfTronSectionsDataToExcelData(pdfTronSectionsData: ISectionData[]): AnnualReportExcelSheet[] {
        const sheetsData: AnnualReportExcelSheet[] = [];
        const notesExists: boolean = pdfTronSectionsData.some((obj) => obj['sectionId'] === PDFSectionIds.NOTES);

        for (const sectionData of pdfTronSectionsData) {
            const sectionId = sectionData.sectionId;
            let needsReference = false;
            if (!SECTIONS_tO_EXPORT_AS_EXCEL.includes(sectionId)) {
                continue;
            }

            if (sectionData.data == null) {
                throw new Error(`This section ${sectionId} must have data`);
            }

            let data: IFinancialTableData[] | null;
            if (sectionId === PDFSectionIds.BALANCE) {
                data = this.mapBalanceSheetDataToFinancialTableData(sectionData.data as IBalanceSheetData);
            } else if (sectionId === PDFSectionIds.APBS || sectionId === PDFSectionIds.APIS) {
                data = this.mapAccountProofData(sectionData.data as IFinancialTableData[]);
                needsReference = true;
            } else {
                data = sectionData.data as IFinancialTableData[];
            }

            const sheetTables = this.getSheetTablesData(data, sectionId, notesExists || needsReference);
            const sheetData: AnnualReportExcelSheet = {
                name: sectionId,
                tables: sheetTables,
            };

            sheetsData.push(sheetData);
        }

        return sheetsData;
    }

    private getSheetTablesData(
        financialTableData: IFinancialTableData[],
        sectionId: PDFSectionIds,
        notesExists: boolean,
    ): AnnualReportExcelSheetTable[] {
        const excelSheetTablesData: AnnualReportExcelSheetTable[] = [];

        const spliteDataByTableHeader = this.spliteDataByTableHeader(financialTableData);

        for (const table of spliteDataByTableHeader) {
            const headerRow = table.shift();

            const headers = this.getSheetTableHeaders(headerRow);
            const tableRows = this.getSheetTableRows(table, sectionId, notesExists);

            const excelSheetTable: AnnualReportExcelSheetTable = {
                headers,
                rows: tableRows,
            };

            excelSheetTablesData.push(excelSheetTable);
        }

        return excelSheetTablesData;
    }

    private mapBalanceSheetDataToFinancialTableData(balanceSheetData: IBalanceSheetData): IFinancialTableData[] {
        const financialTableData: IFinancialTableData[] = [];

        financialTableData.push(...balanceSheetData.assets);
        financialTableData.push(...balanceSheetData.liabilitiesAndEquity);

        return financialTableData;
    }

    private mapAccountProofData(accountProofData: IFinancialTableData[]): IFinancialTableData[] {
        return accountProofData.map((data) => {
            let rValue;

            if (data.r !== undefined) {
                // Keep `r` as is if it exists
                rValue = data.r;
            } else if (data.extraHeader1) {
                // Use `extraHeader1` if it exists and is not empty
                rValue = data.extraHeader1;
            } else {
                // Otherwise, set `r` to an empty string
                rValue = '-';
            }

            return {
                ...data,
                r: rValue,
            };
        });
    }

    private spliteDataByTableHeader(financialTableData: IFinancialTableData[]): IFinancialTableData[][] {
        const indexesOfTableHeaders: number[] = [];
        const spliteDataByTableHeader: IFinancialTableData[][] = [];

        for (let index = 0; index < financialTableData.length; index++) {
            const element = financialTableData[index];

            if (element.rowType === RowTypes.TABLE_HEAD) {
                indexesOfTableHeaders.push(index);
            }
        }

        for (let index = 0; index < indexesOfTableHeaders.length; index++) {
            const beginingOfBoundry = indexesOfTableHeaders[index];

            let endOfBoundry: number;

            if (index < indexesOfTableHeaders.length - 1) {
                const nextElementIndex = index + 1;
                endOfBoundry = indexesOfTableHeaders[nextElementIndex];
            } else if (index === indexesOfTableHeaders.length - 1) {
                endOfBoundry = financialTableData.length;
            }
            const slicedDataByTableHeaderIndexes = financialTableData.slice(beginingOfBoundry, endOfBoundry);
            spliteDataByTableHeader.push(slicedDataByTableHeaderIndexes);
        }

        return spliteDataByTableHeader;
    }

    private getSheetTableHeaders(financialTableData: IFinancialTableData): string[] {
        const headers: string[] = [];

        if (financialTableData.t != null) {
            headers.push(financialTableData.t);
        }

        if (financialTableData.r != null) {
            headers.push(financialTableData.r);
        }

        if (financialTableData.cV != null) {
            headers.push(financialTableData.cV);
        }

        if (financialTableData.cP != null) {
            headers.push(financialTableData.cP);
        }

        if (financialTableData.pV != null) {
            headers.push(financialTableData.pV);
        }

        if (financialTableData.pP != null) {
            headers.push(financialTableData.pP);
        }

        return headers;
    }

    private convertToNumber(value: string, useCommaAsDecimal: boolean): number | undefined {
        // Remove thousand separators based on the determination
        if (useCommaAsDecimal) {
            value = value.replace(/[\.'’`]/g, ''); // '.' and '’' are thousand separators
        } else {
            value = value.replace(/,'’`/g, ''); // ',' is thousand separator
        }
        if (useCommaAsDecimal) {
            value = value.replace(/,/g, '.');
            const dotMatches = value.match(/\./g);
            const dotCount = dotMatches ? dotMatches.length : 0;

            // Replace all dots except for the last one
            if (dotCount > 1) {
                let replaceCount = dotCount - 1;
                value = value.replace(/\./g, (match) => (replaceCount-- > 0 ? '' : match));
            }
        }
        value = value.replace(/[^0-9.-]/g, ''); // Remove any other non-digit characters except for the dot and minus if existed
        return value === '-' ? 0 : NumberUtils.roundNumber(value);
    }

    private getValueOrZero(dataValue: string | null | undefined, useCommaAsDecimal: boolean): number | undefined {
        if (dataValue != null && dataValue.length !== EMPTY_ARRAY_LENGTH) {
            const isNumber = !isNaN(Number(dataValue.replace(/[^0-9.]/g, '')));
            if (isNumber) {
                return this.convertToNumber(dataValue, useCommaAsDecimal);
            } else {
                return 0;
            }
        }
        return undefined;
    }

    private getSheetTableRows(
        financialTableData: IFinancialTableData[],
        sectionId: PDFSectionIds,
        notesExists: boolean,
    ): AnnualReportExcelRow[] {
        const excelRowsData: AnnualReportExcelRow[] = [];

        // Determine comma separator
        let commaAsDecimalCount = 0;
        let dotAsDecimalCount = 0;
        for (const data of financialTableData) {
            if (data.cV) {
                if (data.cV.lastIndexOf(',') > data.cV.lastIndexOf('.')) {
                    commaAsDecimalCount++;
                } else if (data.cV.lastIndexOf('.') > data.cV.lastIndexOf(',')) {
                    dotAsDecimalCount++;
                }
            }
        }
        const useCommaAsDecimal = commaAsDecimalCount > dotAsDecimalCount;

        for (const data of financialTableData) {
            const text = data.t;

            const closingValue = this.getValueOrZero(data.cV, useCommaAsDecimal);
            const closingPercentageValue = this.getValueOrZero(data.cP, useCommaAsDecimal);
            const openingValue = this.getValueOrZero(data.pV, useCommaAsDecimal);
            const openingPercentageValue = this.getValueOrZero(data.pP, useCommaAsDecimal);
            let reference: string | undefined = SECTIONS_WITH_NO_REFERENCE.includes(sectionId) ? undefined : '';
            if (data.r != null && data.r.length !== EMPTY_ARRAY_LENGTH) {
                reference = data.r;
            }
            const rowType = data.rowType;
            const AnnualReportExcelRow: AnnualReportExcelRow = {
                text,
                closingValue,
                closingPercentageValue,
                openingValue,
                openingPercentageValue,
                type: rowType,
            };

            if (notesExists) {
                AnnualReportExcelRow['reference'] = reference;
            }
            excelRowsData.push(AnnualReportExcelRow);
        }

        return excelRowsData;
    }

    public prepareNotesDataForExcelStructuredExport(
        sections: INotesSection[],
        excelData: IPdfTronData,
        language: string,
        CYDate: string,
        PYDate: string,
        numberedTitles: INotesComponentsNumberedTitles[][],
        placeholderMap: Record<string, string>,
        /**
         * FX (REV-21512): the print DTO carries super tables as rendered HTML, which Excel cannot
         * write into cells — so the structured rebuild below needs the display-currency twins
         * computed the same way the preview computes them. Null on non-FX reports (G0).
         */
        fx: IFxSuperTablePrintContext | null = null,
        annualReport: IAnnualReport | null = null,
    ): IPdfTronData {
        console.log('Preparing notes data for Excel export...');
        if (!excelData.hasNts) {
            return excelData;
        }

        // FX (REV-21512): one tree pass for the whole export — the twins of every super table are
        // derived from it below. Absent context (non-FX report) leaves the export untouched.
        const fxTrees = fx != null && annualReport != null ? this.superTableMapper.buildLiveTrees(annualReport, fx) : null;
        // Columns and values are gated by the SAME condition: exporting the twin columns while the
        // enrichment did not run (e.g. a report without both statements) would emit empty cells.
        const fxForExport = fx != null && fxTrees != null ? { context: fx.context, trees: fxTrees } : null;

        // Loop over subsections, and items.
        // excelData.nts.subsections was built by the pdf-tron mapper with
        // NotesVisibilityHelper.removeHiddenNotes, which drops accordions whose
        // components are all hidden. `sections` still contains those accordions,
        // so we must skip all-hidden accordions and walk subsections with a
        // parallel index to keep alignment.
        let excelSubsectionIndex = 0;
        for (const [outerIndex, accordion] of sections.entries()) {
            const visibleComponents = accordion.data.filter((component) => !component.isHidden);
            if (visibleComponents.length === 0) {
                continue;
            }
            const excelSubsection = excelData.nts.subsections[excelSubsectionIndex++];
            if (!excelSubsection) {
                continue;
            }
            const visibleNumberedTitles = numberedTitles[outerIndex].filter(
                (_numbering, index) => !accordion.data[index].isHidden,
            );
            for (const [innerIndex, component] of visibleComponents.entries()) {
                // Get corresponding item in excelData
                const excelItem = excelSubsection.items[innerIndex];
                if (component.componentName === ComponentType.SUPER_TABLE) {
                    const numbering = visibleNumberedTitles[innerIndex];
                    // Create a new object which then can be rendered in excel
                    // FX: hand the recomputed clone over, so the structured rows carry the
                    // display-currency twins and the resolved rate rows.
                    const superTableData =
                        fxForExport != null
                            ? this.notesDisplayMapper.applyFxTwinsForExport(
                                  component.data,
                                  fxForExport.trees,
                                  fxForExport.context,
                              )
                            : component.data;
                    excelItem.superTableStructuredDataForExcelExport = this.getSheetTableStructuredDataForExcelExport(
                        superTableData,
                        language,
                        CYDate,
                        PYDate,
                        numbering,
                        fxForExport?.context.displayCurrency,
                    );
                } else if (component.componentName === ComponentType.RICH_TEXT) {
                    const translatedText = component.data.find((d) => d.language === language);
                    
                    // Replace placeholders with actual values (same logic as PDF export)
                    const textWithReplacedPlaceholders = this.replacePlaceholders(
                        translatedText.text as string, 
                        placeholderMap,
                        excelData.currency,
                        excelData.organization.country,
                        excelData.signatures
                    );
                    
                    // Simply store the text as a normal string, instead of HTML
                    const tempDiv = document.createElement('div');
                    tempDiv.innerHTML = textWithReplacedPlaceholders;
                    const textContent = tempDiv.textContent || tempDiv.innerText || '';
                    excelItem.data = textContent;
                }
            }
        }

        return excelData;
    }

    private getSheetTableStructuredDataForExcelExport(
        data,
        language,
        CYDate,
        PYDate,
        numbering: INotesComponentsNumberedTitles,
        fxDisplayCurrency?: string,
    ): SuperTableStructuredData {
        // FX (REV-21512): the display-currency twin columns are metadata-only — their values are
        // computed, never stored. They export only when the caller supplied a recomputed table
        // (see `applyFxTwinsForExport`); without it they would render empty, so they stay out —
        // the same rule the print preview applies.
        const xAxisConfig =
            fxDisplayCurrency != null
                ? data.options.xAxisConfig
                : data.options.xAxisConfig.filter((col) => !SuperTableHelper.isFxColumnType(col.type));
        let structuredRows: SuperTableStructuredData = {
            columnLength: xAxisConfig.length,
            rows: [],
        };

        // Add segment row if segments exist
        if (data.options.segments && data.options.segments.length > 0) {
            const segmentRow = this.createSegmentRow(data.options.segments, xAxisConfig, language);
            if (segmentRow && !data.options.transpose) {
                structuredRows.rows.push(segmentRow);
            }
        }

        let counter = 0;
        // Title row is based on xAxisConfig
        const title =
            numbering && numbering.tableNumbers[0]
                ? `${numbering.tableNumbers[0]} ${data.options.title[language]}`
                : data.options.title[language];
        let titleRow = {
            rowType: 'Table Header',
            col0: title,
        };
        counter = 1;
        for (const col of xAxisConfig) {
            // FX (REV-21512): a twin column carries the same date label as its functional original
            // and, like CY/PV, no `title` of its own — so it must take the date branch too.
            if (col.type === 'CY' || col.type === SuperTableColumnType.CURRENT_YEAR_FX) {
                if (data.options.showFiscalYear) {
                    titleRow['col' + counter] = new Date(CYDate).getFullYear().toString();
                } else {
                    titleRow['col' + counter] = new Date(CYDate).toLocaleDateString();
                }
            } else if (col.type === 'PV' || col.type === SuperTableColumnType.PREVIOUS_YEAR_FX) {
                if (data.options.showFiscalYear) {
                    titleRow['col' + counter] = new Date(PYDate).getFullYear().toString();
                } else {
                    titleRow['col' + counter] = new Date(PYDate).toLocaleDateString();
                }
            } else {
                titleRow['col' + counter] = col.title?.[language] ?? '';
            }
            // FX: an unsaved twin column has no own currency — fall back to the display currency,
            // exactly like the editor and the print preview do.
            const currencyLabel =
                col.currency ?? (SuperTableHelper.isFxColumnType(col.type) ? fxDisplayCurrency : undefined);
            if (data.options.showCurrency && currencyLabel) {
                titleRow['col' + counter] += '\n' + currencyLabel;
            }
            counter++;
        }
        structuredRows.rows.push(titleRow);
        counter = 0;

        // Loop over the rows
        for (const row of data.rows) {
            let structuredRow = { rowType: row.rowType, col0: row.title };

            for (let [i, col] of xAxisConfig.entries()) {
                i++; // Because col0 is title
                structuredRow['col' + i] = this.getValueGivenRowAndColumn(row, col.columnId);
            }
            structuredRows.rows.push(structuredRow);
        }

        if (data.options.transpose) {
            structuredRows = this.transposeStructuredData(structuredRows);
        }
        // If Footnotes exist, add them as well with type "Footnote"
        for (const footnote of data.footnotes) {
            let structuredRow = { rowType: 'Footnote', col0: footnote };
            structuredRows.rows.push(structuredRow);
        }
        return structuredRows;
    }

    private createSegmentRow(segments: any[], xAxisConfig: any[], language: string): any | null {
        if (!segments || segments.length === 0) {
            return null;
        }

        let segmentRow = {
            col0: '', 
        };

        // Initialize all columns as empty
        for (let i = 1; i <= xAxisConfig.length; i++) {
            segmentRow['col' + i] = '';
        }

        // Process each segment
        for (const segment of segments) {
            if (segment.childColIds && segment.childColIds.length > 0 && segment.title) {
                // Get the first child column ID to determine the starting position
                const firstChildColId = segment.childColIds[0];
                
                // Find the column number where this segment should be placed
                const columnIndex = xAxisConfig.findIndex(col => 
                    col.columnId === firstChildColId || 
                    col.type === firstChildColId
                );
                
                if (columnIndex !== -1) {
                    // Column index in xAxisConfig + 1 because col0 is reserved for title
                    const colNumber = columnIndex + 1;
                    
                    // Set the segment title at the first matching column
                    const segmentTitle = segment.title[language] || segment.title['de-ch'] || '';
                    segmentRow['col' + colNumber] = segmentTitle;
                    
                }
            }
        }

        return segmentRow;
    }

    private getValueGivenRowAndColumn(row, columnType) {
        if (row.type === 'SPACE') {
            return null;
        }
        if (columnType === 'CY') {
            return row.currentValue;
        } else if (columnType === 'PV') {
            return row.previousValue;
        } else if (
            columnType === SuperTableColumnType.CURRENT_YEAR_FX ||
            columnType === SuperTableColumnType.PREVIOUS_YEAR_FX
        ) {
            // FX (REV-21512): user rows export what was typed (it IS stored under the column id),
            // everything else the twin `applyFxTwinsForExport` computed — the same split the print
            // preview renders.
            if (SuperTableHelper.isFxUserValueRowType(row.type)) {
                return row[columnType];
            }
            const fxRow = row as IFxPrintRow;

            return columnType === SuperTableColumnType.CURRENT_YEAR_FX ? fxRow.__fxPrintCY : fxRow.__fxPrintPY;
        } else {
            return row[columnType];
        }
    }

    private transposeStructuredData(data: SuperTableStructuredData): SuperTableStructuredData {
        const transposedRows: any[] = [];
        const numRows = data.rows.length;
        const numCols = data.columnLength + 1; // +1 for the title column (col0)
        
        for (let colIndex = 0; colIndex < numCols; colIndex++) {
            const newRow: any = {};
            for (let rowIndex = 0; rowIndex < numRows; rowIndex++) {
                const currentRow = data.rows[rowIndex];
                const cellValue = currentRow['col' + colIndex];
                newRow['col' + rowIndex] = cellValue;
            }
            transposedRows.push(newRow);
        }
        
        return {
            columnLength: numRows - 1, // -1 because the first row is now the title column
            rows: transposedRows
        };
    }

    /**
     * Simplified placholder replacement as implemented in PlaceholderVistor for PDFTronI
     * Replaces placeholder mentions with actual values from the placeholder map
     * Based on PlaceholderReplacerVisitor logic used for PDF exports
     */
    private replacePlaceholders(
        html: string, 
        placeholderMap: Record<string, string>,
        currency: string,
        organizationCountry: string,
        signatures: ISignatureData
    ): string {
        if (!html || !placeholderMap) {
            return html;
        }

        return html.replace(
            /<span class="mention" data-mention="@[\w.-]+">@[\w.-]+<\/span>/g,
            (match) => {
                // Extract the placeholder key from data-mention attribute
                const regex = /data-mention="([^"]*)"/;
                const dataMentionAttribute = match.match(regex);
                
                if (!dataMentionAttribute || !dataMentionAttribute[1]) {
                    return match; // Return original if no match found
                }
                
                const mentionTagName = dataMentionAttribute[1];
                
                // Handle special placeholders first (same as PlaceholderReplacerVisitor)
                switch (mentionTagName) {
                    case '@ar.currency':
                        return currency != null ? currency : '';
                    case '@org.country':
                        return organizationCountry != null ? organizationCountry : '';
                    case '@ar.signatory1':
                        return signatures.signature1 != null ? signatures.signature1 : '';
                    case '@ar.signatory2':
                        return signatures.signature2 != null ? signatures.signature2 : '';
                    case '@ar.place-of-signing':
                        return signatures.place != null ? signatures.place : '';
                    case '@ar.date-of-signing':
                        return signatures.date != null ? signatures.date : '';
                }
                
                const placeholder = placeholderMap[mentionTagName];
                
                // Replace with placeholder value if it exists, otherwise keep original
                return placeholder != null ? placeholder : match;
            },
        );
    }
}
